import { useCallback, useEffect, useRef, useState } from "react";
import type { Omittable } from "../../lib/props";

/** One rendered day group, in render order (top → bottom on screen). */
export interface DateJumpGroup {
  /** YYYY-MM-DD key — must match the `${idPrefix}${dateKey}` DOM id. */
  dateKey: string;
  /** Human label shown in the floating sticky pill. */
  dateLabel: string;
}

export type UseDateJumpParams = Omittable<{
  /** Ref to the feed's scroll container. The hook walks up to the real
   *  scroller when that element does not overflow itself. */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** Day groups in render order. Drives the floating sticky-pill label. */
  groups: DateJumpGroup[];
  /** Whether more pages can be loaded (for paginate-to-date). */
  hasMore: boolean;
  /** Loads the next page; resolves once it has committed. */
  loadMore?: () => Promise<unknown>;
  /** DOM-id prefix wrapping each group, e.g. `date-group-`. */
  idPrefix?: string;
  /** Height of one DateSeparator row (paddingTop sm + pill + paddingBottom lg). */
  separatorHeight?: number;
  /** Fired synchronously when a jump begins (e.g. release stick-to-bottom). */
  onJumpStart?: () => void;
  /** Fired when a jump's pagination loop settles. */
  onJumpEnd?: () => void;
}>;

export interface UseDateJump {
  /** Smooth-scrolls to a day, paginating forward until it mounts if needed. */
  scrollToDate: (dateKey: string) => Promise<void>;
  /** Label of the group currently pinned under the top edge, or null. */
  stickyDateLabel: string | null;
  /** The element that actually scrolls (the feed's own element, or an overflowing ancestor). */
  getScrollElement: () => HTMLElement | null;
  /** True while a jump's pagination loop runs — auto-loaders should defer. */
  isJumpingRef: React.MutableRefObject<boolean>;
}

const DEFAULT_ID_PREFIX = "date-group-";
const DEFAULT_SEPARATOR_HEIGHT = 52;
// Small lead-in so the static separator has breathing room above it instead
// of being pinned to the very top edge.
const SCROLL_LEAD_IN = 16;

/**
 * Generic jump-to-date + sticky-date behavior for any day-grouped scrolling
 * feed. The feed renders <DateSeparator>/<DatePill> with this hook's
 * `scrollToDate`, wraps each group in `${idPrefix}${dateKey}`, and renders a
 * floating <DatePill> when `stickyDateLabel` is set. No domain knowledge.
 */
export function useDateJump({
  scrollRef,
  groups,
  hasMore,
  loadMore,
  idPrefix = DEFAULT_ID_PREFIX,
  separatorHeight = DEFAULT_SEPARATOR_HEIGHT,
  onJumpStart,
  onJumpEnd,
}: UseDateJumpParams): UseDateJump {
  const [stickyDateLabel, setStickyDateLabel] = useState<string | null>(null);

  // scrollToDate paginates in a loop until the target day's group appears.
  // It must read the latest hasMore and call the latest loadMore each
  // iteration, otherwise the closure goes stale and re-fetches the same page
  // forever.
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const onJumpStartRef = useRef(onJumpStart);
  onJumpStartRef.current = onJumpStart;
  const onJumpEndRef = useRef(onJumpEnd);
  onJumpEndRef.current = onJumpEnd;

  // Synchronous re-entry guard. Without this a second invocation (StrictMode
  // replay, double-fire from a tap event, an auto-loader racing the loop)
  // can run a parallel pagination loop that fires the same page twice.
  const isJumpingRef = useRef(false);

  // Returns the element that's *actually* scrolling. Usually the feed's own
  // container. When the feed is nested in a surface that owns the scroll (a
  // Drawer's panel, a ScrollArea viewport), the feed's own element sizes to
  // its content (scrollHeight === clientHeight) and never scrolls — walking up
  // to the nearest overflowing ancestor finds the real scroller.
  const getScrollElement = useCallback((): HTMLElement | null => {
    const ref = scrollRef.current;
    if (!ref || !(ref instanceof HTMLElement)) return null;
    if (ref.scrollHeight > ref.clientHeight) return ref;
    let parent: HTMLElement | null = ref.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const canScroll =
        style.overflowY === "auto" || style.overflowY === "scroll";
      if (canScroll && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return ref;
  }, [scrollRef]);

  const updateStickyDate = useCallback(() => {
    const container = getScrollElement();
    if (!container) return;
    const containerTop = container.getBoundingClientRect().top;

    // Treat a group as active if its top is at or within separatorHeight below
    // containerTop. Without this buffer, scrolling to a date with a small
    // lead-in (so the static separator sits a few px below the top) would fall
    // back to the previous day's group as "active" and the floating pill would
    // show the wrong label.
    let activeGroup: DateJumpGroup | null = null;
    for (const group of groups) {
      const el = document.getElementById(`${idPrefix}${group.dateKey}`);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= containerTop + separatorHeight) {
        activeGroup = group;
      }
    }

    if (!activeGroup) {
      setStickyDateLabel(null);
      return;
    }

    // Hide while the separator is still (partially) visible — avoids two pills.
    const activeEl = document.getElementById(`${idPrefix}${activeGroup.dateKey}`);
    if (
      activeEl &&
      activeEl.getBoundingClientRect().top > containerTop - separatorHeight
    ) {
      setStickyDateLabel(null);
      return;
    }

    setStickyDateLabel(activeGroup.dateLabel);
  }, [groups, getScrollElement, idPrefix, separatorHeight]);

  // Register the scroll listener that drives the floating sticky pill.
  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    el.addEventListener("scroll", updateStickyDate, { passive: true });
    return () => el.removeEventListener("scroll", updateStickyDate);
  }, [getScrollElement, updateStickyDate]);

  const scrollToDate = useCallback(
    async (dateKey: string): Promise<void> => {
      // Synchronous re-entry guard — bail if a previous jump is still running.
      if (isJumpingRef.current) return;

      const container = getScrollElement();
      if (!container) return;

      const tryScroll = (): boolean => {
        const el = document.getElementById(`${idPrefix}${dateKey}`);
        if (!el) return false;
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const offset =
          elRect.top - containerRect.top + container.scrollTop - SCROLL_LEAD_IN;
        container.scrollTo({ top: offset, behavior: "smooth" });
        return true;
      };

      // Hold the jump gate across the whole attempt so any auto-loader (e.g. an
      // IntersectionObserver) that checks isJumpingRef stays out of the way.
      // onJumpStart/onJumpEnd always pair — even on an immediate hit — so a
      // consumer that pauses its loader on start reliably resumes on end.
      isJumpingRef.current = true;
      onJumpStartRef.current?.();
      try {
        if (tryScroll()) return;

        // Target day isn't loaded yet — paginate forward until its group
        // mounts, then scroll.
        while (hasMoreRef.current) {
          await loadMoreRef.current?.();
          // Yield a frame so React commits the new items before we look for
          // the group element.
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
          if (tryScroll()) return;
        }

        // Final attempt after the last page loaded.
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );
        tryScroll();
      } finally {
        isJumpingRef.current = false;
        onJumpEndRef.current?.();
      }
    },
    [getScrollElement, idPrefix],
  );

  return { scrollToDate, stickyDateLabel, getScrollElement, isJumpingRef };
}
